import { memo, useMemo, type JSX } from 'react';
import { useEngine } from '../../state/engineStore';
import { SYMBOLS, type SymbolCtx } from './symbols';
import type { LayoutView } from '../../engine/plant/types';

/**
 * Config-driven P&ID renderer. There is no hand-authored SVG per plant: positions come
 * from the layout, the shape comes from the equipment type, and everything that moves
 * is a function of the current snapshot (CLAUDE.md rules 1 and 6).
 */

interface Props {
  view: LayoutView;
  onOpenLoop(id: string): void;
  onOpenInstrument(id: string): void;
}

/** Width of a label/value pair in the value strip, in layout units. */
const VALUE_BOX_WIDTH = 190;

/** Flow-direction dashes. The offset is derived from sim time × flow, not from a timer. */
function Pipe({
  points,
  flow,
  simTime,
  utility,
  label,
}: {
  points: number[];
  flow: number;
  simTime: number;
  utility?: boolean;
  label?: string;
}): JSX.Element {
  const d = useMemo(() => {
    let s = '';
    for (let i = 0; i + 1 < points.length; i += 2) {
      s += `${i === 0 ? 'M' : 'L'} ${points[i]} ${points[i + 1]} `;
    }
    return s;
  }, [points]);
  const active = Math.abs(flow) > 0.5;
  const colour = utility ? 'var(--utility)' : 'var(--fluid)';
  // Dash speed is proportional to flow, capped so a runaway does not strobe.
  const speed = Math.min(Math.abs(flow) / 60, 6) * Math.sign(flow || 1);
  const offset = -(simTime * speed * 6) % 12;
  const mid = points.length >= 4 ? [points[0] as number, points[1] as number] : [0, 0];
  return (
    <g>
      <path d={d} fill="none" stroke="var(--line-dim)" strokeWidth={utility ? 2 : 4.5} strokeLinejoin="round" strokeLinecap="round" />
      {active && (
        <path
          d={d}
          fill="none"
          stroke={colour}
          strokeWidth={utility ? 1.6 : 2.6}
          strokeDasharray={utility ? '4 5' : '7 5'}
          strokeDashoffset={offset}
          strokeLinecap="butt"
          opacity={0.95}
        />
      )}
      {label && (
        <text x={(mid[0] as number) + 6} y={(mid[1] as number) - 7} fontSize={9} fill="var(--text-mute)">
          {label}
        </text>
      )}
    </g>
  );
}

function Bubble({
  tag: tagRef,
  x,
  y,
  leaderTo,
  shared,
  state,
  onClick,
}: {
  tag: string;
  x: number;
  y: number;
  leaderTo?: [number, number];
  shared?: boolean;
  state: { mode?: string; priority?: string; unacked?: boolean };
  onClick(): void;
}): JSX.Element {
  const alarmColour =
    state.priority === 'emergency'
      ? 'var(--pri-emergency)'
      : state.priority === 'high'
        ? 'var(--pri-high)'
        : state.priority === 'medium'
          ? 'var(--pri-medium)'
          : state.priority === 'low'
            ? 'var(--pri-low)'
            : null;
  const [head, tail] = tagRef.includes('-') ? [tagRef.split('-')[0], tagRef.split('-')[1]] : [tagRef, ''];
  return (
    <g transform={`translate(${x} ${y})`} onClick={onClick} style={{ cursor: 'pointer' }}>
      {leaderTo && (
        <line
          x1={0}
          y1={0}
          x2={leaderTo[0] - x}
          y2={leaderTo[1] - y}
          stroke="var(--line-dim)"
          strokeWidth={1}
          strokeDasharray="3 3"
        />
      )}
      {shared && <rect x={-17} y={-17} width={34} height={34} fill="var(--panel)" stroke="var(--line)" strokeWidth={1.3} />}
      <circle r={17} fill="var(--panel)" stroke={alarmColour ?? 'var(--line)'} strokeWidth={alarmColour ? 2.6 : 1.4} className={state.unacked ? 'flash' : undefined} />
      <text x={0} y={-3} textAnchor="middle" fontSize={10} fill="var(--text)" fontFamily="var(--mono)">
        {head}
      </text>
      <text x={0} y={9} textAnchor="middle" fontSize={10} fill="var(--text)" fontFamily="var(--mono)">
        {tail}
      </text>
      {state.mode && (
        <text x={0} y={29} textAnchor="middle" fontSize={8.5} fill="var(--text-mute)" fontFamily="var(--mono)">
          {state.mode}
        </text>
      )}
    </g>
  );
}

export const PidView = memo(function PidView({ view, onOpenLoop, onOpenInstrument }: Props): JSX.Element {
  const values = useEngine((s) => s.values);
  const indexOf = useEngine((s) => s.indexOf);
  const snap = useEngine((s) => s.snap);
  const plant = useEngine((s) => s.plant);
  const simTime = snap?.simTime ?? 0;

  const v = (name: string): number => {
    const i = indexOf.get(name);
    return i === undefined ? 0 : (values[i] ?? 0);
  };

  const equipType = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of plant?.equipment ?? []) m.set(e.id, e.type);
    return m;
  }, [plant]);

  const alarmByTagPrefix = useMemo(() => {
    const m = new Map<string, { priority: string; unacked: boolean }>();
    for (const a of snap?.alarms ?? []) {
      if (a.state === 'normal') continue;
      const prefix = a.tag.split('.')[0] ?? '';
      const unacked = a.state === 'unackedActive' || a.state === 'unackedRtn';
      const cur = m.get(prefix);
      if (!cur || unacked) m.set(prefix, { priority: a.priority, unacked });
    }
    return m;
  }, [snap]);

  const loopMode = useMemo(() => {
    const m = new Map<string, string>();
    for (const l of snap?.loops ?? []) m.set(l.id, l.mode);
    return m;
  }, [snap]);

  const [vx, vy, vw, vh] = view.viewBox;

  return (
    <svg
      viewBox={`${vx} ${vy} ${vw} ${vh}`}
      style={{ width: '100%', height: '100%', display: 'block' }}
      preserveAspectRatio="xMidYMid meet"
    >
      <rect x={vx} y={vy} width={vw} height={vh} fill="var(--bg)" />

      {view.pipes.map((p) => (
        <Pipe
          key={p.id}
          points={p.points}
          flow={p.flowTag ? v(p.flowTag) : 0}
          simTime={simTime}
          utility={p.utility}
          label={p.label}
        />
      ))}

      {(view.labels ?? []).map((l, i) => (
        <text
          key={i}
          x={l.x}
          y={l.y}
          fontSize={l.size ?? 12}
          textAnchor={l.anchor ?? 'middle'}
          fill={l.muted ? 'var(--text-mute)' : 'var(--text-dim)'}
          letterSpacing={1.2}
        >
          {l.text}
        </text>
      ))}

      {view.nodes.map((n) => {
        const type = n.symbol ?? equipType.get(n.equipment) ?? '';
        const render = SYMBOLS[type];
        if (!render) return null;
        const al = alarmByTagPrefix.get(n.equipment);
        const ctx: SymbolCtx = {
          v,
          alarm: al ? (al.unacked ? 'unacked' : 'acked') : 'none',
          label: n.label ?? n.equipment,
          simTime,
        };
        return (
          <g
            key={n.equipment}
            transform={`translate(${n.x} ${n.y}) rotate(${n.rot ?? 0}) scale(${n.scale ?? 1})`}
          >
            {render(n.equipment, ctx)}
          </g>
        );
      })}

      {(view.values ?? []).map((b, i) => (
        <g key={i}>
          <text x={b.x} y={b.y} fontSize={10} fill="var(--text-mute)" textAnchor="start">
            {b.label}
          </text>
          <text
            x={b.x + VALUE_BOX_WIDTH}
            y={b.y}
            fontSize={11.5}
            fontFamily="var(--mono)"
            fill="var(--text)"
            textAnchor="end"
          >
            {v(b.tag).toFixed(b.decimals ?? 2)}
            {b.units ? ` ${b.units}` : ''}
          </text>
        </g>
      ))}

      {view.bubbles.map((b) => {
        const isLoop = loopMode.has(b.ref);
        const al = alarmByTagPrefix.get(b.ref);
        return (
          <Bubble
            key={b.ref + b.x}
            tag={b.ref}
            x={b.x}
            y={b.y}
            leaderTo={b.leaderTo}
            shared={b.shared}
            state={{ mode: loopMode.get(b.ref), priority: al?.priority, unacked: al?.unacked }}
            onClick={() => (isLoop ? onOpenLoop(b.ref) : onOpenInstrument(b.ref))}
          />
        );
      })}
    </svg>
  );
});
